import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  FileText, 
  Upload, 
  Trash2, 
  CheckCircle2, 
  AlertCircle, 
  Sparkles, 
  BookOpen, 
  GraduationCap, 
  Clock, 
  Globe, 
  Target, 
  Layers, 
  ListChecks, 
  HelpCircle,
  ChevronDown,
  ChevronUp,
  FileCheck,
  Zap,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  UploadedMaterial, 
  DocumentRole, 
  StudyMaterialAnalysis, 
  ExamPrepOptions, 
  readUploadedFile, 
  formatFileSize, 
  analyzeStudyMaterials 
} from '@/lib/studyMaterials';
import { OneShotSubmitParams } from './OneShotInputSection';

interface StudyMaterialsSectionProps {
  onSubmit: (params: OneShotSubmitParams) => void;
  isLoading: boolean;
}

const DOCUMENT_ROLES: { role: DocumentRole; label: string; desc: string }[] = [
  { role: 'Syllabus', label: 'Syllabus / Curriculum', desc: 'Official syllabus or course outline' },
  { role: 'Class Notes', label: 'Class / Lecture Notes', desc: 'Handwritten or typed student notes' },
  { role: 'Teacher Material', label: 'Teacher / Faculty Material', desc: 'Teacher slides, handouts, or notes' },
  { role: 'Question Bank', label: 'Question Bank', desc: 'Important questions or practice bank' },
  { role: 'Previous Paper', label: 'Previous Year Paper', desc: 'Past exam or sample paper' },
  { role: 'Assignment', label: 'Assignment / Worksheet', desc: 'Classwork, homework or worksheets' },
  { role: 'Textbook', label: 'Textbook Chapter', desc: 'Textbook pages or chapter reference' },
  { role: 'Other', label: 'Other Resource', desc: 'General study material' },
];

const PREP_LEVELS = [
  { id: 'zero', label: 'Starting from Zero', desc: 'Needs full foundational teaching & prerequisites' },
  { id: 'basics', label: 'Know Fundamentals', desc: 'Knows basics, needs deep concepts & worked examples' },
  { id: 'studied_revision', label: 'Studied & Need Revision', desc: 'Needs structured recap & formula practice' },
  { id: 'exam_revision', label: 'Exam Revision Only', desc: 'High-yield crash prep & exam problem focus' },
];

const STUDY_TIMES = [
  { id: '30m', label: '30 Mins Crash', desc: 'High-yield essential concepts' },
  { id: '1h', label: '1 Hour Exam Prep', desc: 'Focused core topics & practice' },
  { id: '2h', label: '2 Hours Masterclass', desc: 'Complete deep learning & questions' },
  { id: 'masterclass', label: 'Full Deep Masterclass', desc: 'Exhaustive syllabus coverage' },
];

const EXAM_TIMELINES = [
  { id: 'tomorrow', label: 'Exam Tomorrow', desc: 'One day before exam priority mode' },
  { id: '2days', label: 'In 2 Days', desc: 'Balanced 48-hour prep' },
  { id: '1week', label: 'In 1 Week', desc: 'Comprehensive study pace' },
  { id: 'flexible', label: 'Flexible / General', desc: 'Standard learning pace' },
];

const LANGUAGES = [
  "English", "Hindi", "Bengali", "Gujarati", "Kannada", "Malayalam", "Marathi", "Odia", "Punjabi", "Tamil", "Telugu", 
  "Spanish", "French", "German", "Mandarin", "Japanese", "Korean", 
  "Arabic", "Portuguese", "Russian", "Italian"
];

export function StudyMaterialsSection({ onSubmit, isLoading }: StudyMaterialsSectionProps) {
  const [materials, setMaterials] = useState<UploadedMaterial[]>([]);
  const [isReadingFiles, setIsReadingFiles] = useState(false);
  const [prepLevel, setPrepLevel] = useState<'zero' | 'basics' | 'studied_revision' | 'exam_revision'>('basics');
  const [availableTime, setAvailableTime] = useState<'30m' | '1h' | '2h' | 'masterclass'>('2h');
  const [examTimeline, setExamTimeline] = useState<'tomorrow' | '2days' | '1week' | 'flexible'>('tomorrow');
  const [language, setLanguage] = useState<string>('English');

  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<StudyMaterialAnalysis | null>(null);

  // Accordion state for syllabus map preview
  const [showSyllabusDetails, setShowSyllabusDetails] = useState(false);

  // Handle file upload selection
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setIsReadingFiles(true);
    setAnalysisError(null);

    const filesArray = Array.from(e.target.files) as File[];
    const newMaterials: UploadedMaterial[] = [];

    for (const file of filesArray) {
      const mat = await readUploadedFile(file);
      newMaterials.push(mat);
    }

    setMaterials(prev => [...prev, ...newMaterials]);
    setIsReadingFiles(false);
    // Reset file input
    e.target.value = '';
  };

  const handleRoleChange = (id: string, newRole: DocumentRole) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, role: newRole } : m));
  };

  const handleRemoveMaterial = (id: string) => {
    setMaterials(prev => prev.filter(m => m.id !== id));
    if (analysisResult) {
      setAnalysisResult(null); // Clear previous analysis if materials changed
    }
  };

  const handleRunAnalysis = async () => {
    if (materials.length === 0) {
      setAnalysisError("Please upload at least one study material document.");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      const userApiKey = localStorage.getItem('gemini_api_key') || '';
      const googleToken = localStorage.getItem('google_access_token') || '';
      const sessionId = localStorage.getItem('google_session_id') || '';

      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (userApiKey.trim()) headers["x-gemini-api-key"] = userApiKey.trim();
      if (googleToken.trim()) headers["x-google-access-token"] = googleToken.trim();
      if (sessionId.trim()) headers["x-session-id"] = sessionId.trim();

      const options: ExamPrepOptions = {
        prepLevel,
        availableTime,
        examTimeline,
        language
      };

      const result = await analyzeStudyMaterials(materials, options, headers);
      setAnalysisResult(result);
    } catch (err: any) {
      console.error("Study materials analysis failed:", err);
      setAnalysisError(err.message || "Failed to analyze study materials. Please check your network and API credentials.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerateMasterclass = () => {
    if (!analysisResult) return;

    const options: ExamPrepOptions = {
      prepLevel,
      availableTime,
      examTimeline,
      language
    };

    onSubmit({
      subject: analysisResult.subject,
      classLevel: analysisResult.classLevel,
      board: analysisResult.board,
      chapterTopic: analysisResult.chapterOrCourseTitle || 'Exam Preparation Masterclass',
      language,
      studyMaterialsContext: {
        materials,
        analysis: analysisResult,
        options
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      <div className="bg-slate-50/80 border-2 border-dashed border-indigo-200 hover:border-indigo-400 rounded-2xl p-6 text-center transition-all">
        <input
          type="file"
          id="study-material-files"
          multiple
          onChange={handleFileUpload}
          disabled={isLoading || isAnalyzing || isReadingFiles}
          className="hidden"
          accept=".pdf,.txt,.md,.json,.csv,.doc,.docx,.png,.jpg,.jpeg,.webp"
        />
        <label
          htmlFor="study-material-files"
          className="cursor-pointer flex flex-col items-center justify-center gap-3"
        >
          <div className="w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center shadow-inner">
            <Upload className="w-7 h-7" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800 font-montserrat">
              Upload Study Materials & Exam Documents
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Upload Syllabus PDFs, Class Notes, Teacher Materials, Question Banks, Worksheets, or Textbook Pages.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-md hover:bg-indigo-700 transition-colors mt-1">
            <FileText className="w-4 h-4" />
            Select Files to Upload
          </span>
        </label>
      </div>

      {/* Uploaded Materials List */}
      {materials.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-emerald-600" />
              Uploaded Study Materials ({materials.length})
            </h4>
            <span className="text-[11px] text-slate-500 font-medium">
              Select roles to assign source priority
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {materials.map((mat) => (
              <div
                key={mat.id}
                className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs flex items-center justify-between gap-3 hover:border-indigo-300 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0 font-bold text-xs">
                    {mat.mimeType.includes('pdf') ? 'PDF' : 'DOC'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-800 truncate" title={mat.fileName}>
                      {mat.fileName}
                    </p>
                    <p className="text-[11px] text-slate-400 font-medium">
                      {formatFileSize(mat.fileSize)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={mat.role}
                    onChange={(e) => handleRoleChange(mat.id, e.target.value as DocumentRole)}
                    disabled={isLoading || isAnalyzing}
                    className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-[11px] font-bold text-slate-700 focus:outline-none focus:border-indigo-500 cursor-pointer"
                  >
                    {DOCUMENT_ROLES.map((r) => (
                      <option key={r.role} value={r.role}>
                        {r.label}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => handleRemoveMaterial(mat.id)}
                    disabled={isLoading || isAnalyzing}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                    title="Remove file"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Configuration Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50/50 p-4 rounded-xl border border-slate-200/80">
        {/* Prep Level */}
        <div>
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-2">
            1. Current Preparation Level
          </label>
          <div className="grid grid-cols-1 gap-1.5">
            {PREP_LEVELS.map((pl) => (
              <button
                key={pl.id}
                type="button"
                onClick={() => setPrepLevel(pl.id as any)}
                disabled={isLoading || isAnalyzing}
                className={cn(
                  "flex items-center justify-between p-2 rounded-lg text-xs font-semibold text-left transition-all border cursor-pointer",
                  prepLevel === pl.id
                    ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                )}
              >
                <span>{pl.label}</span>
                <span className={cn(
                  "text-[10px]",
                  prepLevel === pl.id ? "text-indigo-100" : "text-slate-400"
                )}>
                  {pl.desc}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Available Time & Timeline */}
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-2">
              2. Available Study Time
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              {STUDY_TIMES.map((st) => (
                <button
                  key={st.id}
                  type="button"
                  onClick={() => setAvailableTime(st.id as any)}
                  disabled={isLoading || isAnalyzing}
                  className={cn(
                    "p-2 rounded-lg text-xs font-bold text-center border cursor-pointer transition-all",
                    availableTime === st.id
                      ? "bg-purple-600 text-white border-purple-600 shadow-sm"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                  )}
                >
                  {st.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-2">
              3. Exam Timeline & Language
            </label>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={examTimeline}
                onChange={(e) => setExamTimeline(e.target.value as any)}
                disabled={isLoading || isAnalyzing}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 cursor-pointer"
              >
                {EXAM_TIMELINES.map((tl) => (
                  <option key={tl.id} value={tl.id}>{tl.label}</option>
                ))}
              </select>

              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isLoading || isAnalyzing}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 cursor-pointer"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Trigger & Status */}
      {materials.length > 0 && !analysisResult && (
        <div className="pt-2">
          {analysisError && (
            <div className="mb-3 p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs font-medium flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-500" />
              <span>{analysisError}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleRunAnalysis}
            disabled={isLoading || isAnalyzing || materials.length === 0}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 hover:from-indigo-700 hover:to-purple-700 text-white font-montserrat font-bold py-3.5 px-6 rounded-xl text-sm shadow-lg shadow-indigo-500/20 disabled:opacity-50 cursor-pointer transition-all"
          >
            {isAnalyzing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Professor X is Analyzing Materials & Syllabus...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                <span>Analyze Materials & Generate Exam Plan</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Pre-Generation Analysis Result Card */}
      <AnimatePresence>
        {analysisResult && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 text-white rounded-2xl p-6 shadow-2xl border border-indigo-500/30 space-y-5"
          >
            <div className="flex items-center justify-between border-b border-indigo-800/60 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center text-amber-300">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-bold font-montserrat tracking-tight text-white flex items-center gap-2">
                    Professor X Exam Plan Ready
                    <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full">
                      {analysisResult.totalDocumentsProcessed} Materials Analyzed
                    </span>
                  </h3>
                  <p className="text-xs text-indigo-200">
                    {analysisResult.subject} • {analysisResult.classLevel} ({analysisResult.board})
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setAnalysisResult(null)}
                className="text-xs text-slate-400 hover:text-white underline cursor-pointer"
              >
                Re-analyze
              </button>
            </div>

            {/* High Priority Topics Badges */}
            <div>
              <p className="text-[11px] font-bold text-amber-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-amber-400" />
                High Priority Exam Topics Detected
              </p>
              <div className="flex flex-wrap gap-2">
                {analysisResult.highPriorityTopics?.map((topic, i) => (
                  <span
                    key={i}
                    className="text-xs font-bold bg-amber-500/10 text-amber-200 border border-amber-500/30 px-3 py-1 rounded-lg flex items-center gap-1"
                  >
                    ★ {topic}
                  </span>
                ))}
              </div>
            </div>

            {/* Syllabus Map Accordion */}
            {analysisResult.syllabusMap && analysisResult.syllabusMap.length > 0 && (
              <div className="bg-indigo-950/60 border border-indigo-800/50 rounded-xl p-3">
                <button
                  type="button"
                  onClick={() => setShowSyllabusDetails(!showSyllabusDetails)}
                  className="w-full flex items-center justify-between text-xs font-bold text-indigo-200 cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-indigo-400" />
                    Syllabus Coverage Map ({analysisResult.syllabusMap.length} Units)
                  </span>
                  {showSyllabusDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showSyllabusDetails && (
                  <div className="mt-3 space-y-2 border-t border-indigo-800/40 pt-2 text-xs">
                    {analysisResult.syllabusMap.map((unit, idx) => (
                      <div key={idx} className="bg-slate-900/50 p-2.5 rounded-lg border border-indigo-900/40">
                        <p className="font-bold text-indigo-300">{unit.unitTitle}</p>
                        <p className="text-[11px] text-slate-300 mt-1">
                          Topics: {unit.topics?.join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Questions & Insights Summary */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-indigo-900/30 border border-indigo-700/40 p-3 rounded-xl">
                <p className="text-[10px] font-bold text-indigo-300 uppercase">Exam Questions Found</p>
                <p className="text-lg font-extrabold text-white mt-0.5">
                  {analysisResult.crossDocumentInsights?.detectedQuestions?.length || 0} Questions
                </p>
              </div>

              <div className="bg-indigo-900/30 border border-indigo-700/40 p-3 rounded-xl">
                <p className="text-[10px] font-bold text-indigo-300 uppercase">Primary Source Truth</p>
                <p className="text-xs font-bold text-emerald-300 mt-1 truncate">
                  Teacher Notes & Syllabus
                </p>
              </div>
            </div>

            {/* Launch Masterclass Button */}
            <button
              type="button"
              onClick={handleGenerateMasterclass}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-slate-950 font-montserrat font-extrabold py-4 px-6 rounded-xl text-sm shadow-xl shadow-amber-500/20 cursor-pointer transition-all"
            >
              <Zap className="w-5 h-5 text-slate-950 fill-slate-950" />
              <span>GENERATE MY EXAM PREP MASTERCLASS</span>
              <ArrowRight className="w-5 h-5 text-slate-950" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
